import {
    ComponentProps,
    ComponentPropsWithoutRef,
    ElementType,
    MouseEvent as ReactMouseEvent,
    ReactElement,
    ComponentType,
} from 'react';
import { WidthProps } from 'styled-system';

import { Spinner } from '../../common/Spinner/Spinner';

import { AbsoluteCenter } from './components/AbsoluteCenter';
import { ButtonChildrenWrapper } from './components/ButtonChildrenWrapper';
import { ButtonContentWrapper } from './components/ButtonContentWrapper';
import { StyledButton } from './components/StyledBaseButton';
import { getIconSize } from './util/getIconSize';
import { getSpinnerSize } from './util/getSpinnerSize';

export interface ButtonProps extends ComponentPropsWithoutRef<'button'>, WidthProps {
    emphasis?: 'high' | 'medium' | 'low' | 'none';
    size?: 'small' | 'medium';
    destructive?: boolean;
    isLoading?: boolean;
    LeadingIcon?: ComponentType<{ width?: number; height?: number }>;
    TrailingIcon?: ComponentType<{ width?: number; height?: number }>;
}

const BaseButton = ({
    emphasis = 'medium',
    size = 'medium',
    destructive = false,
    isLoading = false,
    LeadingIcon,
    TrailingIcon,
    onClick,
    children,
    ...rest
}: ButtonProps) => {
    const internalOnClick = (event: ReactMouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (isLoading) {
            event.preventDefault();
            return;
        }

        onClick && onClick(event);
    };

    const iconSize = getIconSize(size);

    return (
        <StyledButton
            emphasis={emphasis}
            size={size}
            destructive={destructive}
            isLoading={isLoading}
            onClick={internalOnClick}
            {...rest}
        >
            <ButtonContentWrapper isLoading={isLoading}>
                {LeadingIcon ? <LeadingIcon width={iconSize} height={iconSize} /> : null}

                <ButtonChildrenWrapper>{children}</ButtonChildrenWrapper>

                {TrailingIcon ? <TrailingIcon width={iconSize} height={iconSize} /> : null}
            </ButtonContentWrapper>

            {isLoading ? (
                <AbsoluteCenter>
                    <Spinner color="currentColor" size={getSpinnerSize(size)} />
                </AbsoluteCenter>
            ) : null}
        </StyledButton>
    );
};

BaseButton.displayName = 'BaseButton';

interface ButtonComponentType {
    <C extends ElementType = 'button'>(
        props: ButtonProps &
            Omit<ComponentProps<C>, keyof ButtonProps> & {
                as?: C;
            }
    ): ReactElement;

    displayName?: string;
}

export const Button = BaseButton as ButtonComponentType;
